import { http } from '@/api/http';
import type { OperationResult, PageEnvelope } from '@/api/shared';

import type { PersonDto, PersonSummaryDto } from '@/features/patient-record/api';

/**
 * Global / quick / paginated person-search endpoints. The hand-written page
 * envelopes mirror the live Spring shape; see `PageEnvelope` for the canonical
 * generic.
 */

export type PersonPageResponseDto = PageEnvelope<PersonDto>;
export type PersonSummaryPageResponseDto = PageEnvelope<PersonSummaryDto>;

/** Query params for `GET /persons/search`. All optional; the BE ignores absent ones. */
export interface SearchPersonsParams {
  readonly last_name?: string;
  readonly identifier_value?: string;
  readonly dob_year?: number;
  readonly dob_month?: number;
  readonly dob_day?: number;
  readonly page?: number;
  readonly size?: number;
}

export const patientSearchApi = {
  /**
   * GET /persons?pageNumber=&pageSize= — paginated person list (live PhenoTips core).
   * Used by global quick search to build a client-filterable patient index.
   */
  listPersons: (
    params: { pageNumber?: number; pageSize?: number } = {},
    options?: { signal?: AbortSignal },
  ): Promise<PersonPageResponseDto> =>
    http.get<PersonPageResponseDto>('/persons', {
      ...options,
      query: {
        pageNumber: params.pageNumber ?? 0,
        pageSize: params.pageSize ?? 500,
      },
    }),

  /**
   * GET /persons/search — paginated patient search. Used by the create-patient
   * duplicate guard (Step 0 / Step 2). All params optional; the BE filters by
   * the ones provided. Default `page=0, size=50` matches the live default.
   */
  searchPersons: (
    params: SearchPersonsParams = {},
    options?: { signal?: AbortSignal },
  ): Promise<PersonSummaryPageResponseDto> => {
    const query: Record<string, string | number> = {};
    if (params.last_name !== undefined && params.last_name.length > 0) {
      query['last_name'] = params.last_name;
    }
    if (params.identifier_value !== undefined && params.identifier_value.length > 0) {
      query['identifier_value'] = params.identifier_value;
    }
    if (params.dob_year !== undefined) query['dob_year'] = params.dob_year;
    if (params.dob_month !== undefined) query['dob_month'] = params.dob_month;
    if (params.dob_day !== undefined) query['dob_day'] = params.dob_day;
    query['page'] = params.page ?? 0;
    query['size'] = params.size ?? 50;
    return http.get<PersonSummaryPageResponseDto>('/persons/search', { ...options, query });
  },

  /** GET /search?q=…&limit=… — global patient + family search. */
  search: (
    params: { q: string; limit?: number },
    options?: { signal?: AbortSignal },
  ): Promise<OperationResult<'search'>> =>
    http.get<OperationResult<'search'>>('/search', {
      ...options,
      query: {
        q: params.q,
        ...(params.limit !== undefined ? { limit: params.limit } : {}),
      },
    }),

  /** GET /recent-patients?limit=… — patients the current user recently accessed. */
  getRecentPatients: (
    params: { limit?: number } = {},
    options?: { signal?: AbortSignal },
  ): Promise<OperationResult<'getRecentPatients'>> =>
    http.get<OperationResult<'getRecentPatients'>>('/recent-patients', {
      ...options,
      query: {
        ...(params.limit !== undefined ? { limit: params.limit } : {}),
      },
    }),
};
